import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form-v7';
import { RadioGroup, RadioGroupItem, cn } from '@parrot-software/design-system';

import { StepLayout } from '@onboarding/presentation/components/shared';
import { TAX_RATES } from '@onboarding/domain/value-objects';
import type { WizardFormValues } from '@onboarding/presentation/types';

const TAX_KEYS = [TAX_RATES.STANDARD, TAX_RATES.BORDER] as const;

/**
 * Step 5 of the onboarding wizard: Tax rate selection.
 * Renders a vertical list of radio cards with label and description.
 */
export const TaxStep = () => {
  const { t } = useTranslation('onboarding');
  const { control } = useFormContext<WizardFormValues>();

  const options = useMemo(
    () =>
      TAX_KEYS.map((key) => ({
        value: key,
        label: t(`steps.tax.options.${key}`),
        description: t(`steps.tax.options.${key}Description`),
      })),
    [t],
  );

  return (
    <StepLayout description={t('steps.tax.description')} title={t('steps.tax.title')}>
      <Controller
        control={control}
        name="tax"
        render={({ field }) => (
          <RadioGroup className="flex flex-col gap-4" onValueChange={field.onChange} value={field.value}>
            {options.map((option) => {
              const isSelected = field.value === option.value;
              return (
                <label
                  className={cn(
                    'flex cursor-pointer items-center gap-3 rounded-lg border p-4 shadow-xs transition-colors',
                    isSelected ? 'border-primary' : 'border-border hover:border-gray-300',
                  )}
                  key={option.value}
                >
                  <div className="flex flex-1 flex-col gap-0.5">
                    <span className="text-base font-medium leading-6 text-foreground">{option.label}</span>
                    <span className="text-sm font-normal leading-5 text-muted-foreground">{option.description}</span>
                  </div>
                  <RadioGroupItem id={option.value} value={option.value} />
                </label>
              );
            })}
          </RadioGroup>
        )}
      />
    </StepLayout>
  );
};
